import {
  getNearbyPincodes as getNearbyPincodesFromService,
  findNearestPincode,
} from "./pincodeService";

const NOMINATIM_URL = "https://nominatim.openstreetmap.org";
const POSTAL_API_BASE = "https://api.postalpincode.in";
const UA = "PGApp/1.0 (pgfinder@example.com)";
const API_BASE_URL = "https://ciplpgapi.azurewebsites.net";
const NOMINATIM_TIMEOUT_MS = 5000;

// Cache for geocoding results - has a TTL and never caches failures, so a
// transient bad answer (e.g. Overpass timing out mid-lookup) can't get
// permanently "frozen in" for the rest of a user's session. Without this,
// one unlucky moment gets remembered as truth until the tab is reloaded.
const geocodeCache = new Map();
const GEOCODE_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

const getCached = (key) => {
  const entry = geocodeCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.cachedAt > GEOCODE_CACHE_TTL_MS) {
    geocodeCache.delete(key);
    return null;
  }
  return entry.value;
};

const setCached = (key, value) => {
  // Only cache genuine successes - a null/failed pincode lookup should
  // always retry fresh next time, not get remembered as "no pincode here".
  if (!value || !value.pincode) return;
  geocodeCache.set(key, { value, cachedAt: Date.now() });
};

// ========== Helper: fetch with a hard timeout ==========
// Any call to a shared public service needs a timeout - without one, a
// slow/unresponsive server hangs the whole search instead of failing
// gracefully in a few seconds.
const fetchWithTimeout = async (url, options = {}, timeoutMs = NOMINATIM_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      throw new Error(`Request timed out after ${timeoutMs}ms: ${url}`);
    }
    throw error;
  }
};

// ========== Helper: India Post's own directory ==========
// The authoritative source (data.gov.in) - doesn't depend on OpenStreetMap's
// community tagging being either complete OR correct. This is what fixes
// cases like "Karol Bagh" resolving to 110060 (a neighboring area's
// pincode) instead of its real 110005 - that wrong answer came from an
// OSM point mistagged at the exact coordinate Nominatim matched. India
// Post's directory isn't subject to that per-building tagging error.
const getPostOfficesByName = async (name) => {
  try {
    const url = `${POSTAL_API_BASE}/postoffice/${encodeURIComponent(name)}`;
    const response = await fetchWithTimeout(url, {}, 4000);
    if (!response.ok) return [];

    const data = await response.json();
    if (!Array.isArray(data) || data[0]?.Status !== "Success") return [];

    return data[0].PostOffice || [];
  } catch (error) {
    console.warn("India Post API error:", error.message);
    return [];
  }
};

// ========== Helper: Get accurate pincode from coordinates (fallback only) ==========
// Used only when the India Post name lookup finds nothing.
const getPincodeFromCoordinates = async (lat, lng) => {
  try {
    const reverseUrl = `${NOMINATIM_URL}/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1&zoom=18`;
    const response = await fetchWithTimeout(reverseUrl, {
      headers: { "User-Agent": UA, Accept: "application/json" },
    });
    const data = await response.json();

    return data?.address?.postcode || data?.address?.postal_code || null;
  } catch (error) {
    console.warn("Reverse geocode error:", error.message);
    return null;
  }
};

// ========== MAIN: Geocode place (pan-India, no hardcoded city bias) ==========
export const geocodePlace = async (query) => {
  const cacheKey = query.toLowerCase().trim();
  const cached = getCached(cacheKey);
  if (cached) {
    console.log(`\n🔍 Geocoding: "${query.trim()}" (cache hit, from ${Math.round((Date.now() - geocodeCache.get(cacheKey).cachedAt) / 1000)}s ago)`);
    return cached;
  }

  const searchQuery = query.trim();

  console.log(`\n🔍 Geocoding: "${searchQuery}"`);

  // No city name is ever appended, and no bounding box is applied -
  // restricting to India as a whole (not a city) keeps results relevant
  // pan-India instead of hard-locking every search to one region.
  const url = `${NOMINATIM_URL}/search?q=${encodeURIComponent(
    searchQuery
  )}&format=json&limit=5&addressdetails=1&countrycodes=in&dedupe=1`;

  console.log(`  Trying: "${searchQuery}"`);

  let bestResult = null;
  let bestLat = null;
  let bestLng = null;

  try {
    const response = await fetchWithTimeout(url, {
      headers: { "User-Agent": UA, Accept: "application/json" },
    });

    if (response.ok) {
      const data = await response.json();
      if (data && data.length > 0) {
        bestResult = data[0];
        bestLat = parseFloat(bestResult.lat);
        bestLng = parseFloat(bestResult.lon);
        console.log(`    Found: ${bestResult.display_name?.split(",")[0]}`);
      }
    }
  } catch (e) {
    console.warn(`    Error:`, e.message);
  }

  if (!bestResult) {
    console.log(`❌ No results found for "${searchQuery}"`);
    return null;
  }

  console.log(`✅ Best match: ${bestResult.display_name?.split(",")[0]}`);
  console.log(`📍 Coordinates: ${bestLat}, ${bestLng}`);

  const nominatimState = bestResult.address?.state;

  // Try the MOST SPECIFIC locality name first. The raw text the user
  // actually typed goes FIRST. Deliberately EXCLUDES district-level
  // fields (city_district, state_district, county) AND plain "city" -
  // in a huge metro (Delhi, Mumbai, Bangalore), address.city is just
  // "Delhi"/"Mumbai"/"Bangalore" for EVERY point inside it, so matching
  // it against India Post grabs some arbitrary head office for the whole
  // metro (e.g. "Delhi G.P.O." -> 110006) - exactly the same shape of
  // bug as the district issue, just one level less broad. searchQuery
  // and the matched place's own display name still correctly cover
  // cases like "Karnal" (a smaller city where the searched name IS the
  // right answer) without needing a blind address.city fallback.
  const candidateNames = [
    searchQuery,
    bestResult.address?.suburb,
    bestResult.address?.neighbourhood,
    bestResult.address?.quarter,
    bestResult.address?.village,
    bestResult.address?.town,
    bestResult.display_name?.split(",")[0],
  ].filter(Boolean);

  const uniqueCandidates = [...new Set(candidateNames)];

  let pincode = null;
  let nearbyPincodes = [];

  // Fire all candidate lookups in parallel so trying several names costs
  // the same wall-clock time as trying one.
  const candidateResults = await Promise.all(
    uniqueCandidates.map((name) => getPostOfficesByName(name))
  );

  let postOffices = [];
  let matchedName = null;

  for (let i = 0; i < uniqueCandidates.length; i++) {
    let candidateOffices = candidateResults[i];

    if (nominatimState && candidateOffices.length > 0) {
      const sameState = candidateOffices.filter(
        (po) => po.State?.toLowerCase() === nominatimState.toLowerCase()
      );
      if (sameState.length > 0) candidateOffices = sameState;
    }

    if (candidateOffices.length > 0) {
      postOffices = candidateOffices;
      matchedName = uniqueCandidates[i];
      break; // stop at the FIRST (most specific) name that matched
    }
  }

  if (postOffices.length > 0) {
    const headOffice =
      postOffices.find((po) => po.BranchType === "Head Post Office") ||
      postOffices[0];

    pincode = headOffice.Pincode;
    nearbyPincodes = Array.from(
      new Set(postOffices.map((po) => po.Pincode).filter((p) => p && p !== pincode))
    ).slice(0, 4);

    console.log(`  📮 Pincode (India Post, matched on "${matchedName}"): ${pincode}`);
  }

  // Fall back to OSM reverse-geocode only if India Post had nothing at all
  if (!pincode) {
    pincode = await getPincodeFromCoordinates(bestLat, bestLng);
    if (!pincode && bestResult.address?.postcode) {
      pincode = bestResult.address.postcode;
    }
    if (pincode) {
      console.log(`  📮 Pincode (OSM fallback): ${pincode}`);
    }
  }

  // True last resort: a small-radius (1-5km) proximity search for the
  // nearest real tagged postcode. Only reached if BOTH India Post's name
  // directory AND Nominatim's own postcode tag came up empty - e.g. a
  // popular colloquial name like "Pitampura" that isn't itself a post
  // office name, sitting at a point OSM also didn't tag directly.
  if (!pincode) {
    pincode = await findNearestPincode(bestLat, bestLng);
    if (pincode) {
      console.log(`  📮 Pincode (nearest-in-radius fallback): ${pincode}`);
    } else {
      console.log(`  ⚠️ No pincode found via India Post, OSM, or proximity search`);
    }
  }

  const result = {
    lat: bestLat,
    lng: bestLng,
    pincode: pincode,
    nearbyPincodes: nearbyPincodes,
    label: bestResult.display_name?.split(",")[0] || searchQuery,
    fullAddress: bestResult.display_name,
  };

  console.log(
    `📍 Final: ${result.label} | Pincode: ${result.pincode || "NOT FOUND"}\n`
  );

  setCached(cacheKey, result);
  return result;
};

// ========== Helper: hit the backend listing API with a given pincode list ==========
const fetchListings = async (searchQuery, price, pgType, pinCodes) => {
  const requestBody = {
    SearchText: `${searchQuery}`,
    PGRentRange: price || null,
    PGType: pgType || null,
    PinCode: pinCodes && pinCodes.length > 0 ? pinCodes : null,
  };

  console.log("📨 API REQUEST BODY:", JSON.stringify(requestBody, null, 2));

  const apiResponse = await fetch(`${API_BASE_URL}/pgmaster/listing-landing-page`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });

  if (!apiResponse.ok) {
    throw new Error(`API responded with status ${apiResponse.status}`);
  }

  const apiData = await apiResponse.json();
  console.log(`📦 API returned ${apiData?.length || 0} PGs`);

  let results = [];
  if (apiData && Array.isArray(apiData)) results = apiData;
  else if (apiData?.data) results = apiData.data;
  else if (apiData?.recordsets?.[0]) results = apiData.recordsets[0];

  return { results, requestBody };
};

// ========== MAIN SEARCH FUNCTION - two-phase, non-blocking ==========
// Phase 1 (awaited, fast): geocode -> main pincode -> hit backend -> return.
// Phase 2 (fire-and-forget, background): find nearby pincodes -> hit
// backend again with the broadened list -> report only the NEW PGs via
// onNearbyResults, so the caller can append them without a full reload.
//
// onNearbyResults(payload) is called AT MOST once, only if nearby
// pincodes were actually found and returned extra data. payload:
//   { nearbyPincodes: string[], results: array (full broadened result set),
//     pincodesSent: string[] }
export const performLocationSearch = async (
  searchQuery,
  gender,
  price,
  sharing,
  pgType = null,
  pgCity = null,
  onNearbyResults = null
) => {
  console.log(`\n🔍 ========== NEW SEARCH ==========`);
  console.log(`🔎 "${searchQuery}"`);
  console.log(
    `📊 Filters: Gender=${gender}, Price=${price}, Sharing=${sharing}, PGType=${pgType}`
  );

  let geoResult = null;
  let pincodeInfo = null;

  try {
    geoResult = await geocodePlace(searchQuery);
  } catch (error) {
    console.log(`⚠️ Geocoding error:`, error.message);
  }

  const mainPincode = geoResult?.pincode || null;
  const mainPincodeList = mainPincode ? [mainPincode] : null;

  if (mainPincode) {
    pincodeInfo = {
      pincode: mainPincode,
      area: geoResult.label,
      coordinates: { lat: geoResult.lat, lng: geoResult.lng },
    };
    console.log(`📮📍 Pincode detected: ${mainPincode} - ${geoResult.label}`);
  } else {
    console.log(`⚠️ No pincode found for "${searchQuery}"`);
  }

  // ---------- PHASE 1: instant result with just the main pincode ----------
  let phase1 = { results: [], requestBody: null };
  try {
    phase1 = await fetchListings(searchQuery, price, pgType, mainPincodeList);
  } catch (error) {
    console.error("Phase 1 API Error:", error);
    return {
      success: false,
      error: "Failed to fetch PG listings",
      resultCount: 0,
      details: error.message,
      searchedLocation: searchQuery,
      pincodeInfo: pincodeInfo,
      pincodesSent: mainPincodeList,
    };
  }

  const phase1Result = {
    success: true,
    results: phase1.results,
    resultCount: phase1.results.length,
    searchedLocation: searchQuery,
    appliedFilters: { gender, price, sharing, pgType, pgCity },
    pincodeInfo: pincodeInfo,
    pincodesSent: mainPincodeList,
    coordinates: geoResult ? { lat: geoResult.lat, lng: geoResult.lng } : null,
    locationName: geoResult?.label || searchQuery,
    nearbyPincodes: [], // filled in by phase 2, later, via the callback
    apiEndpoint: "listing-landing-page",
    requestBody: phase1.requestBody,
  };

  // ---------- PHASE 2: background enrichment, never blocks the return above ----------
  if (mainPincode && geoResult) {
    (async () => {
      let nearby = geoResult.nearbyPincodes || [];

      // If India Post's own directory already gave us related pincodes
      // (essentially free - no extra request), use those. Only reach for
      // the OSM/Overpass fallback (slower, less reliable) if it didn't.
      if (nearby.length === 0 && geoResult.lat && geoResult.lng) {
        try {
          nearby = await getNearbyPincodesFromService(geoResult.lat, geoResult.lng, 3);
        } catch (error) {
          console.log("⚠️ Background nearby-pincode fetch failed:", error.message);
          nearby = [];
        }
      }

      if (!nearby || nearby.length === 0) {
        console.log("ℹ️ No nearby pincodes found - phase 2 has nothing to add");
        return;
      }

      console.log(`🔄 [background] Nearby pincodes found: ${nearby.join(", ")}`);

      const allPincodes = [mainPincode, ...nearby.filter((p) => p !== mainPincode)];

      try {
        const phase2 = await fetchListings(searchQuery, price, pgType, allPincodes);
        if (onNearbyResults) {
          onNearbyResults({
            nearbyPincodes: nearby,
            results: phase2.results,
            pincodesSent: allPincodes,
          });
        }
      } catch (error) {
        console.warn("⚠️ [background] Phase 2 API call failed:", error.message);
      }
    })();
  }

  return phase1Result;
};

// ========== SUGGESTIONS ==========
export const getLocationSuggestions = async (query, limit = 8) => {
  if (!query || query.length < 2) return [];

  const url = `${NOMINATIM_URL}/search?q=${encodeURIComponent(
    query
  )}&format=json&limit=${limit}&addressdetails=1&countrycodes=in&dedupe=1`;

  try {
    const response = await fetchWithTimeout(url, {
      headers: { "User-Agent": UA, Accept: "application/json" },
    });
    const data = await response.json();

    return data.map((item) => ({
      id: item.place_id,
      name: item.display_name.split(",")[0],
      area:
        item.address?.suburb ||
        item.address?.neighbourhood ||
        item.address?.city ||
        item.address?.state ||
        "",
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      pincode: item.address?.postcode || null,
      fullAddress: item.display_name,
    }));
  } catch (error) {
    console.error("Suggestions error:", error);
    return [];
  }
};